export interface Area {
  top: number;
  bottom: number;
  left: number;
  right: number;
}
export interface Placement {
  menuLeft: number;
  menuTop: number;
  /** Where the accessory (the reactions row) goes; meaningless without one. */
  accLeft: number;
  accTop: number;
  /** Set when the menu is taller than the room it has: it scrolls. */
  maxHeight: number | null;
}
const EDGE = 8,
  GAP = 8;

/**
 * Where a menu goes, like WhatsApp Web: downward from `y` when it fits, upward ending at `above`
 * when it does not, always inside `area`. An accessory (the reactions row, `aw` × `ah`) sits on
 * the message's side: above a menu that opens downward, below one that opens upward.
 */
export function placeMenu(input: {
  x: number;
  y: number;
  above?: number | undefined;
  width: number;
  height: number;
  accessory?: { width: number; height: number } | null;
  area: Area;
}): Placement {
  const { x, width: w, height: h, area } = input;
  const top = area.top + EDGE,
    bottom = area.bottom - EDGE;
  const aw = input.accessory?.width ?? 0,
    ah = input.accessory ? input.accessory.height + GAP : 0;
  const down = input.y,
    up = input.above ?? input.y;
  let menuTop: number;
  let accTop: number;
  let maxHeight: number | null = null;
  if (down + h <= bottom && down - ah >= top) {
    menuTop = down;
    accTop = down - ah;
  } else if (up - h >= top && up + ah <= bottom) {
    menuTop = up - h;
    accTop = up + GAP;
  } else if (top + ah + h <= bottom && down - ah < top) {
    // Too close to the top for the row: both move down.
    menuTop = Math.max(down, top + ah);
    accTop = menuTop - ah;
  } else if (top + h + ah <= bottom) {
    // Too close to the bottom for the row: both move up.
    menuTop = Math.min(up, bottom - ah) - h;
    accTop = menuTop + h + GAP;
  } else {
    // Taller than the area: the side with more room, scrolling.
    const below = bottom - down,
      room = up - top;
    maxHeight = Math.max(120, Math.max(below, room) - ah);
    if (below >= room) {
      menuTop = down;
      accTop = down - ah;
    } else {
      menuTop = up - Math.min(h, maxHeight);
      accTop = up + GAP;
    }
  }
  const menuLeft = Math.max(
    area.left + EDGE,
    Math.min(x, area.right - w - EDGE),
  );
  const accLeft = Math.max(
    area.left + EDGE,
    Math.min(menuLeft + w / 2 - aw / 2, area.right - aw - EDGE),
  );
  return { menuLeft, menuTop, accLeft, accTop, maxHeight };
}
